import { useEffect, useRef, useState } from 'react';
import { Button, Group, Loader, Text } from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HttpClient from '../helpers/HttpClient';
import { Booking } from '../types/BookingModel';
import dayjs from 'dayjs';
import { generateAccountReportPdf as generateReportPdf, groupBookingsByPerson, PersonGroup } from '../helpers/accountReportPdf';

function fmt(v: number) {
  return v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function BookingReport() {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<PersonGroup[]>([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasDownloaded = useRef(false);

  const personIds = searchParams.get('persons')
    ? new Set(searchParams.get('persons')!.split(',').map(Number))
    : null;
  const autodownload = searchParams.get('autoprint') === '1';

  useEffect(() => {
    const httpClient = new HttpClient<Booking>('/booking');
    httpClient.getAll().then((result) => {
      const personGroups: PersonGroup[] = groupBookingsByPerson(result)
        .filter((g) => personIds ? personIds.has(g.person?.id ?? -1) : g.net < 0);

      setGroups(personGroups);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!loading && autodownload && !hasDownloaded.current) {
      hasDownloaded.current = true;
      generateReportPdf(groups);
    }
  }, [loading, autodownload]);

  if (loading) return <Loader size="md" />;

  return (
    <>
      <Group mb="md">
        <Button variant="subtle" onClick={() => navigate('/booking/report/summary')}>‹ Zurück zur Übersicht</Button>
        <Text fw={700} size="lg">Verbindlichkeiten Report</Text>
        <Button onClick={() => generateReportPdf(groups)}>PDF herunterladen</Button>
      </Group>

      {groups.map(({ person, bookings, creditSum, debitSum, net }) => (
        <div key={person?.id} style={{ marginBottom: 40 }}>
          <Text fw={700} size="xl">{person?.lastName} {person?.firstName}</Text>
          {person?.iban && <Text size="sm" c="dimmed">IBAN: {person.iban}</Text>}

          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr>
                {['Datum', 'Typ', 'Text', 'Soll', 'Haben'].map((h) => (
                  <th key={h} style={{ textAlign: h === 'Soll' || h === 'Haben' ? 'right' : 'left', border: '1px solid #ccc', padding: '4px 8px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => {
                const isCredit = (b.bookingType?.multiplier ?? 0) > 0;
                return (
                  <tr key={b.id}>
                    <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{dayjs(b.bookingDate).format('DD.MM.YYYY')}</td>
                    <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{b.bookingType?.title}</td>
                    <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{b.bookingText}</td>
                    <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>{!isCredit ? fmt(b.bookingAmount) : ''}</td>
                    <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>{isCredit ? fmt(b.bookingAmount) : ''}</td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 'bold' }}>
                <td colSpan={3} style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>Summe</td>
                <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>{fmt(debitSum)}</td>
                <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>{fmt(creditSum)}</td>
              </tr>
              <tr style={{ fontWeight: 'bold' }}>
                <td colSpan={3} style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>
                  {net < 0 ? 'Verbindlichkeit' : net > 0 ? 'Forderung' : ''}
                </td>
                <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>{net < 0 ? fmt(Math.abs(net)) : ''}</td>
                <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>{net > 0 ? fmt(net) : ''}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}

      {groups.length === 0 && <Text>Keine Verbindlichkeiten vorhanden.</Text>}
    </>
  );
}

export default BookingReport;
